import { Box, InputAdornment, useTheme } from '@ltvco/refresh-lib/theme';
import {
  Control,
  Controller,
  UseFormRegisterReturn,
} from '@ltvco/refresh-lib/vendors';
import SearchIcon from '@mui/icons-material/Search';
import React from 'react';

import { StyledInputBase } from './components';

export interface BaseSearchInputProps {
  /**
   * Registration object from react-hook-form
   */
  registerReturn: UseFormRegisterReturn;
  /**
   * Placeholder text for the input field
   */
  placeholder: string;
  /**
   * Current value of the input field
   */
  value: string;
  /**
   * Name of the field for error handling
   */
  fieldName: string;
  /**
   * Control object from react-hook-form
   */
  control: Control;
  /**
   * Whether to enable autofocus on the input
   */
  autoFocus?: boolean;
  /**
   * Additional props to pass to the input component
   */
  inputProps?: Record<string, unknown>;
  /**
   * Additional props to pass to the input component
   */
  InputProps?: Record<string, unknown>;
  /**
   * Whether the input has an error
   */
  hasError?: boolean;
  /**
   * Custom on change handler
   */
  customOnChange?: (
    // eslint-disable-next-line no-unused-vars
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    // eslint-disable-next-line no-unused-vars
    field: { onChange: (value: string) => void }
  ) => void;
  /**
   * Function to clear errors for the field
   */
  // eslint-disable-next-line no-unused-vars
  clearErrors?: (field?: string) => void;
  /**
   * Callback when input is focused
   */
  // eslint-disable-next-line no-unused-vars
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Callback when input loses focus
   */
  // eslint-disable-next-line no-unused-vars
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const BaseSearchInput = React.memo(
  ({
    registerReturn,
    placeholder,
    fieldName,
    control,
    autoFocus = false,
    inputProps = {},
    InputProps = {},
    hasError = false,
    customOnChange,
    clearErrors,
    onFocus,
    onBlur,
  }: BaseSearchInputProps) => {
    const theme = useTheme();
    const { ref } = registerReturn;
    const handleInputChange = (
      e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
      // eslint-disable-next-line no-unused-vars
      field: { onChange: (value: string) => void }
    ) => {
      if (customOnChange) {
        customOnChange(e, field);
        return;
      }

      const value = e.target.value;
      field.onChange(value);

      // If there's any value, clear errors
      if (clearErrors && value.trim()) {
        clearErrors(fieldName);
      }
    };

    return (
      <Box sx={{ width: '100%' }}>
        <Controller
          control={control}
          name={fieldName}
          render={({ field }) => (
            <StyledInputBase
              inputRef={ref}
              {...field}
              onChange={(e) => handleInputChange(e, field)}
              onFocus={onFocus}
              onBlur={onBlur}
              placeholder={placeholder}
              fullWidth
              error={hasError}
              autoFocus={autoFocus}
              inputProps={inputProps}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        color: hasError
                          ? theme.palette.error.main
                          : theme.palette.primary.main,
                      }}
                    />
                  </InputAdornment>
                ),
                ...InputProps,
              }}
            />
          )}
        />
      </Box>
    );
  }
);
